import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast/use-toast';
import { Navigate, useLocation } from 'react-router-dom';
import { isSubscriptionValid, useGetSubscriptionApi, useGetUserApi, useTimeZone } from '@liquidbudget/common';

type AuthLoaderProps = {
  children: React.ReactNode;
  renderLoading: () => React.ReactNode;
  renderUnauthenticated?: () => React.ReactNode;
  renderError?: (error: Error) => React.ReactNode;
};

export function AuthLoader({
  children,
  renderLoading,
  renderUnauthenticated,
  renderError = (error: Error) => <>{JSON.stringify(error)}</>,
}: AuthLoaderProps) {
  const { data: user, isLoading, isError, error } = useGetUserApi({});

  if (isLoading) {
    return <>{renderLoading()}</>;
  }

  if (isError) {
    return <>{renderError(error as Error)}</>;
  }

  if (!user && renderUnauthenticated) {
    return <>{renderUnauthenticated()}</>;
  }

  return <>{children}</>;
}

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const user = useGetUserApi();
  const location = useLocation();

  if (!user) {
    return <Navigate to={`/login?redirectTo=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return children;
};

export const SubscriptionProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { data: subscription, isLoading } = useGetSubscriptionApi();
  const timeZone = useTimeZone();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (!isSubscriptionValid(timeZone, subscription)) {
    toast({
      title: 'Subscription Required',
      description: 'Please activate your subscription',
    });

    return (
      <Navigate
        to="/dashboard/account-settings"
        state={{
          from: location.pathname,
          subscriptionRequired: true,
        }}
        replace
      />
    );
  }

  return children;
};
